// About.js
import React, { useEffect, useRef } from 'react';
import './About.css';
import { FaBullseye, FaUsers, FaGlobe, FaAward } from 'react-icons/fa';
import founderImg from '../images/founder1.jpeg'; // replace if needed

const About = () => {
  const ioTargetsRef = useRef([]);
  const cardRef = useRef(null);
  const heroRef = useRef(null);
  const countersRef = useRef([]);

  // Helpers to collect refs
  const setIOTarget = (i) => (el) => (ioTargetsRef.current[i] = el);
  const setCounterRef = (i) => (el) => (countersRef.current[i] = el);

  /* ---------------- Reveal on scroll ---------------- */
  useEffect(() => {
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.style.setProperty('--reveal-delay', e.target.dataset.revealDelay || '0ms');
            e.target.classList.add('reveal-in');
            obs.unobserve(e.target);
          }
        });
      },
      { threshold: 0.15 }
    );

    if (!prefersReduced) {
      ioTargetsRef.current.forEach((el) => el && obs.observe(el));
    } else {
      ioTargetsRef.current.forEach((el) => el && el.classList.add('reveal-in'));
    }

    return () => obs.disconnect();
  }, []);

  /* ---------------- 3D tilt on founder card ---------------- */
  const handleCardMove = (e) => {
    if (!cardRef.current) return;
    const r = cardRef.current.getBoundingClientRect();
    const x = (e.clientX ?? (e.touches?.[0]?.clientX || 0)) - r.left;
    const y = (e.clientY ?? (e.touches?.[0]?.clientY || 0)) - r.top;

    const rx = ((y - r.height / 2) / (r.height / 2)) * -6;
    const ry = ((x - r.width / 2) / (r.width / 2)) * 6;
    cardRef.current.style.transform = `perspective(900px) rotateX(${rx}deg) rotateY(${ry}deg) translateY(-6px)`;
  };
  const resetCardTilt = () => {
    if (cardRef.current) cardRef.current.style.transform = '';
  };

  /* ---------------- Parallax hero blobs ---------------- */
  useEffect(() => {
    const el = heroRef.current;
    if (!el) return;
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) return;

    const onMove = (e) => {
      const r = el.getBoundingClientRect();
      const cx = (e.clientX - r.left) / r.width - 0.5;
      const cy = (e.clientY - r.top) / r.height - 0.5;
      el.style.setProperty('--parallaxX', (cx * 12).toFixed(2) + 'px');
      el.style.setProperty('--parallaxY', (cy * 12).toFixed(2) + 'px');
    };
    el.addEventListener('mousemove', onMove);
    return () => el.removeEventListener('mousemove', onMove);
  }, []);

  /* ---------------- Auto-count stats when visible ---------------- */
  useEffect(() => {
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const parseTarget = (el) => Number(el?.dataset?.target || '0');

    const animateCounter = (el, target, duration = 1200) => {
      if (!el) return;
      if (prefersReduced) {
        el.textContent = target.toString();
        return;
      }
      const start = performance.now();
      const from = 0;
      const step = (t) => {
        const p = Math.min(1, (t - start) / duration);
        const eased = 1 - Math.pow(1 - p, 3);
        const val = Math.floor(from + (target - from) * eased);
        el.textContent = val.toString();
        if (p < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    };

    const watcher = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            countersRef.current.forEach((c) => c && animateCounter(c, parseTarget(c)));
            watcher.disconnect();
          }
        });
      },
      { threshold: 0.4 }
    );

    const countersHost = document.querySelector('.about-why-us.compact');
    if (countersHost) watcher.observe(countersHost);
    return () => watcher.disconnect();
  }, []);

  /* ---------------- Scroll to next section via chevron ---------------- */
  const scrollToNext = () => {
    const next = document.querySelector('.founder-spotlight');
    if (next) next.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <main className="about-page">
      {/* Hero */}
      <section
        className="about-hero simple reveal"
        ref={(el) => {
          setIOTarget(0)(el);
          heroRef.current = el;
        }}
        data-reveal-delay="0ms"
        aria-labelledby="about-hero-title"
      >
        <div className="blob blob-1" aria-hidden="true" />
        <div className="blob blob-2" aria-hidden="true" />
        <h1 id="about-hero-title">About Aazira Solutions</h1>
        <p>
          We build reliable, secure, and scalable software that helps businesses grow.
        </p>
        <button
          className="hero-chevron"
          aria-label="Scroll to leadership section"
          onClick={scrollToNext}
        >
          ⌄
        </button>
      </section>

      {/* Founder Spotlight */}
      <section
        className="founder-spotlight reveal"
        ref={setIOTarget(1)}
        data-reveal-delay="80ms"
        aria-labelledby="leadership-title"
      >
        <div className="founder-text">
          <span className="eyebrow" aria-hidden="true">Meet Our Leadership</span>
          <h2 className="headline-underlined" id="leadership-title">Founder &amp; CEO</h2>
          <h3 className="founder-name">Mrs.Umadevi Shinde</h3>
          <p className="founder-bio">
            Mrs.Umadevi Shinde is the Founder &amp; CEO of Aazira Technology and an entrepreneur behind
            two ventures—Cordon Technologies and Pushpalate. With 14+ years in IT delivery, she blends
            product and e-commerce expertise with program management (Six Sigma, PRINCE2) to take ideas
            from concept to scale. A Certified Scrum Master and PMP. excels at client communication
            and team building, leads a 50-member cross-functional organization, and is hands-on with
            Zoho Books, Oracle, Microsoft 365, Smartsheet, SharePoint, and QlikView.
          </p>
        </div>

        <figure className="founder-photo-wrap">
          <div
            className="founder-card interactive"
            ref={cardRef}
            onMouseMove={handleCardMove}
            onMouseLeave={resetCardTilt}
            onTouchMove={handleCardMove}
            onTouchEnd={resetCardTilt}
            aria-label="Founder portrait card with interactive tilt"
          >
            <img
              src={founderImg}
              alt="Portrait of Umadevi Shinde — Founder & CEO"
              className="founder-photo"
              loading="lazy"
            />
            <span className="sheen" aria-hidden="true" />
          </div>
          <figcaption className="sr-only">Interactive portrait card</figcaption>
        </figure>
      </section>

      {/* Why Us (with animated counters) */}
      <section
        className="about-why-us compact reveal"
        ref={setIOTarget(2)}
        data-reveal-delay="120ms"
        aria-labelledby="why-title"
      >
        <h2 id="why-title">Why Choose Aazira?</h2>

        <div className="why-grid">
          <article className="why-card" aria-label="Secure by Design metric">
            <h3>🔒 Secure by Design</h3>
            <p>Security practices embedded from day one.</p>
            <div className="metric" aria-live="polite">
              <span className="metric-number" ref={setCounterRef(0)} data-target="99">0</span>
              <span className="metric-suffix">.9% uptime</span>
            </div>
          </article>

          <article className="why-card" aria-label="Reliable Delivery metric">
            <h3>⚡ Reliable Delivery</h3>
            <p>Iterative releases, clear roadmaps, and strict SLAs.</p>
            <div className="metric" aria-live="polite">
              <span className="metric-number" ref={setCounterRef(1)} data-target="10">0</span>
              <span className="metric-suffix">+ projects</span>
            </div>
          </article>

          <article className="why-card" aria-label="Built to Scale metric">
            <h3>📈 Built to Scale</h3>
            <p>Cloud-native, performance-first architectures.</p>
            <div className="metric" aria-live="polite">
              <span className="metric-number" ref={setCounterRef(2)} data-target="1">0</span>
              <span className="metric-suffix">+ countries</span>
            </div>
          </article>

          <article className="why-card" aria-label="Partner Mindset metric">
            <h3>🤝 Partner Mindset</h3>
            <p>Transparent communication and long-term value.</p>
            <div className="metric" aria-live="polite">
              <span className="metric-number" ref={setCounterRef(3)} data-target="95">0</span>
              <span className="metric-suffix">% CSAT</span>
            </div>
          </article>
        </div>
      </section>

      {/* Mission tiles */}
      <section
        className="about-mission flat reveal"
        ref={setIOTarget(3)}
        data-reveal-delay="160ms"
        aria-labelledby="mission-title"
      >
        <h2 className="sr-only" id="mission-title">Mission, Team, Reach, Values</h2>

        <div className="mission-content" role="article">
          <FaBullseye className="icon" aria-hidden="true" />
          <h3>Our Mission</h3>
          <p>Deliver dependable software that drives real business impact.</p>
        </div>

        <div className="mission-content" role="article">
          <FaUsers className="icon" aria-hidden="true" />
          <h3>Our Team</h3>
          <p>Product thinkers, engineers, and designers who care.</p>
        </div>

        <div className="mission-content" role="article">
          <FaGlobe className="icon" aria-hidden="true" />
          <h3>Global Reach</h3>
          <p>Successful deliveries across multiple countries and domains.</p>
        </div>

        <div className="mission-content" role="article">
          <FaAward className="icon" aria-hidden="true" />
          <h3>Our Values</h3>
          <p>Integrity, craftsmanship, and outcomes over output.</p>
        </div>
      </section>

      {/* Quote */}
      <section
        className="about-quote soft reveal"
        ref={setIOTarget(4)}
        data-reveal-delay="200ms"
        aria-label="Company quote"
      >
        <blockquote>“Simple, secure, and scalable — every product, every time.”</blockquote>
      </section>
    </main>
  );
};

export default About;
